import React, { FC, useState } from 'react';
import './TodoRecords.scss';
import { Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell, TableCellLayout, Button, SelectionEvents, OptionOnSelectData, TableSelectionCell, TableColumnDefinition, createTableColumn, TableColumnId, useTableFeatures, useTableSort, TableColumnSizingOptions, useTableColumnSizing_unstable, TableRowId } from '@fluentui/react-components';

import { format } from 'date-fns';
import {
	ChevronDownRegular,
	ChevronRightRegular,
	EditRegular,
} from "@fluentui/react-icons";
import { Pagination } from '../Pagination/Pagination';
import RowCounterSelector from '../RowCounterSelector/RowCounterSelector';
import { CANCEL_ACTION, ERROR_RECORDS, ERROR_TYPE, INVALIDATE_ACTION, PAGINTATION_SIBLING_COUNT, ROWS_PER_PAGE, VALIDATE_ACTION, WAITING_TYPE } from '../../utils/constants';
import { Link } from 'react-router-dom';
import GenericDialog from '../GenericDialog/GenericDialog';
import RecordsDialog from '../RecordsDialog/RecordsDialog';
import RecordService from '../../services/RecordService';
import { dynamicObjectToFieldConvertor } from '../../utils/helpers';
import { IFieldDetail } from '../../interfaces/IFIeldDetail';
import { RecordsDataSelectable } from '../../types/RecordsDataSelectable';
import { IRecordsData } from '../../interfaces/IRecordsData';
import ConfirmationDialog, { ConfirmationBodyText, ConfirmationTriggers } from '../ConfirmationDialog/ConfirmationDialog';
import { ITriggerProps } from '../GenericDialogContainer/GenericDialogContainer';
import { useDynamicFieldConvertion } from '../../hooks/UseDynamicFieldConvertion';
import TodoOutputsByRecords from '../TodoOutputsByRecords/TodoOutputsByRecords';
import { IRecord } from '../../interfaces/IRecord';

const { parseISO } = require("date-fns");
interface TodoRecordsProps {
	data: Array<IRecordsData>
	currentPage: number;
	allRecordCount?: number;
	rowPerPage?: number;
	onPageChange: any;
	onAmountPageChange: any;
	selectedTab: string;
}

const tableColumns: TableColumnDefinition<RecordsDataSelectable>[] = [
	createTableColumn<RecordsDataSelectable>({
		columnId: "job_id",
		compare: (a, b) => {
			return a.job_id - b.job_id
		},
		renderHeaderCell: () => <>Job Id</>
	}),
	createTableColumn<RecordsDataSelectable>({
		columnId: "record_id",
		compare: (a, b) => {
			return a.id - b.id
		},
		renderHeaderCell: () => <>Record Id</>
	}),
	createTableColumn<RecordsDataSelectable>({
		columnId: "created_on",
		compare: (a, b) => {
			return a.created_on.localeCompare(b.created_on)
		},
		renderHeaderCell: () => <>Created On</>
	}),
	createTableColumn<RecordsDataSelectable>({
		columnId: "processes",
		compare: (a, b) => {
			return a.process.localeCompare(b.process)
		},
		renderHeaderCell: () => <>Processes</>
	}),
	createTableColumn<RecordsDataSelectable>({
		columnId: "subprocesses",
		compare: (a, b) => {
			return a.subprocess.localeCompare(b.subprocess)
		},
		renderHeaderCell: () => <>Subprocesses</>
	}),
	createTableColumn<RecordsDataSelectable>({
		columnId: "input_data",
		renderHeaderCell: () => <>Input Data</>
	}),
	createTableColumn<RecordsDataSelectable>({
		columnId: "output_data",
		renderHeaderCell: () => <>Output Data</>
	}),
	createTableColumn<RecordsDataSelectable>({
		columnId: "actions",
		renderHeaderCell: () => <>Actions</>
	})
]

const TodoRecords: FC<TodoRecordsProps> = (props) => {
	const DEFAULT_ROW_HEIGHT = 48;
	const STYLE_SMALL = "ListStyleSmall"
	const STYLE_BIG = "ListStyleBig"
	const [records, setRecords] = useState<RecordsDataSelectable[]>(
		props.data.map((item: IRecordsData) => ({ ...item, selected: false }))
	)
	const [collapsedRecords, setCollapsedRecords] = useState<{ id: number, collapsed: boolean }[]>(
		records.map((record: RecordsDataSelectable) => ({ id: record.id, collapsed: true }))
	)

	const [sortState, setSortState] = React.useState<{
		sortDirection: "ascending" | "descending";
		sortColumn: TableColumnId | undefined;
	}>({
		sortDirection: "ascending" as const,
		sortColumn: "file",
	});

	const [allCheck, setAllCheck] = useState<boolean>(false)
	const [observations, setObservations] = useState<{ [key: number]: string }>({})
	const [rowHeights, setRowHeights] = useState<{ [key: number]: number }>({})

	const errorTable = props.selectedTab === ERROR_RECORDS
	const showStatus = props.selectedTab === ERROR_RECORDS ? ERROR_TYPE : WAITING_TYPE

	const availableColumns = errorTable ?
		tableColumns.filter(c => c.columnId !== 'obs') :
		tableColumns.filter(c => c.columnId !== 'output_data')

	const [columnSizingOptions] = React.useState<TableColumnSizingOptions>({
		job_id: {
			idealWidth: 120,
			minWidth: 80
		},
		record_id: {
			idealWidth: 120,
			minWidth: 80
		},
		created_on: {
			idealWidth: 180,
			minWidth: 120
		},
		processes: {
			idealWidth: 300,
			minWidth: 150
		},
		subprocesses: {
			idealWidth: 300,
			minWidth: 150
		},
		input_data: {
			idealWidth: 250,
			minWidth: 100
		},
		output_data: {
			idealWidth: 250,
			minWidth: 100
		},
		actions: {
			idealWidth: 80,
			minWidth: 60
		}
	});

	const { dynamicConvertion: dynamicConversionOutputs } = useDynamicFieldConvertion()

	const {
		getRows,
		sort: { getSortDirection, toggleColumnSort, sort },
		columnSizing_unstable,
		tableRef
	} = useTableFeatures(
		{
			columns: tableColumns,
			items: records,
		},
		[
			useTableSort({
				sortState,
				onSortChange: (e, nextSortState) => setSortState(nextSortState),
			}),
			useTableColumnSizing_unstable({ columnSizingOptions })
		]
	);

	const [expandedRows, setExpandedRows] = useState<Set<TableRowId>>(new Set());
	const [outputRecordsByRow, setOutputRecordsByRow] = useState<{[key: string]: RecordsDataSelectable[]}>({});

	const headerSortProps = (columnId: TableColumnId) => ({
		onClick: (e: React.MouseEvent) => toggleColumnSort(e, columnId),
		sortDirection: getSortDirection(columnId),
	});

	const getRecordsByInputRecord = (inputRecord: number) => {
		// Avoid calling if we already have data for this record
		if (outputRecordsByRow[inputRecord.toString()]) {
			return;
		}

		RecordService.getOutputRecordsByInputRecord(inputRecord).then((result: any) => {
			if (result?.results?.length) {
				const allResults = result.results;
				const outputRecords = allResults.map((record: IRecord) => {
					const flds = dynamicConversionOutputs(record.data)
					return {
						id: record.id,
						job_id: record.job?.id || 0,
						created_on: record.createdOn || new Date().toISOString(),
						process: record.process?.name || '',
						subprocess: record.subprocess?.name || '',
						status: record.status || 'pending',
						data: flds,
						selected: false
					}
				})

				setOutputRecordsByRow(prev => ({
					...prev,
					[inputRecord.toString()]: outputRecords
				}));
			}
		}).catch((error: any) => {
			console.error('Error fetching output records:', error);
			setOutputRecordsByRow(prev => ({
				...prev,
				[inputRecord.toString()]: []
			}));
		})
	}

	const getHeaderProps = (columnId: TableColumnId) => {
		const sizingProps = columnSizing_unstable.getTableHeaderCellProps(columnId);
		const sortProps = headerSortProps(columnId);
		return {
			...sizingProps,
			sortDirection: sortProps.sortDirection,
			onClick: (e: React.MouseEvent) => {
				// Check if the click is near the edge for resizing
				const target = e.currentTarget as HTMLElement;
				const rect = target.getBoundingClientRect();
				const clickX = e.clientX;
				const cellRightEdge = rect.right;

				// If clicking within 10px of the right edge, it's likely for resizing
				if (cellRightEdge - clickX > 10) {
					// Not on the resize handle, proceed with sorting
					sortProps.onClick?.(e);
				}
			},
		};
	};

	const handleRowResize = (rowId: number, event: React.MouseEvent) => {
		event.preventDefault();

		const startY = event.clientY;
		const currentHeight = rowHeights[rowId] || DEFAULT_ROW_HEIGHT;

		const handleMouseMove = (e: MouseEvent) => {
			const deltaY = e.clientY - startY;
			const newHeight = Math.max(30, currentHeight + deltaY);

			setRowHeights(prev => ({
				...prev,
				[rowId]: newHeight
			}));
		};

		const handleMouseUp = () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
	};

	const getRowStyle = (rowId: number) => {
		const height = rowHeights[rowId];
		return height ? { height: `${height}px` } : {};
	};

	const triggerRecordAction = (action: string, selectedRecordIds: number[], obs: any) => {
		RecordService
			.validateInvalidateRecords(action, selectedRecordIds, obs)
			.then(result => {
				props.onPageChange(props.currentPage, props.selectedTab)
			})
	}

	const onOptionSelected = (event: SelectionEvents, data: OptionOnSelectData) => {
		props.onAmountPageChange(data.optionValue)
	}

	const onRowSelected = (id: number) => {
		const recs = records.map((record: RecordsDataSelectable) =>
			record.id === id ? ({
				...record, selected: !record.selected
			})
				: record
		)
		setRecords(recs)
		setAllCheck(recs.filter(r => r.selected).length === recs.length)
	}

	const onHeaderSelected = () => {
		const changedStatus = !allCheck;
		setRecords(
			records.map(record => ({ ...record, selected: changedStatus }))
		)
		setAllCheck(changedStatus)
	}

	const anySelectedRecord = () => records.filter(r => r.selected).length

	const toggleRowExpansion = (rowId: TableRowId) => {
		const newExpandedRows = new Set(expandedRows);
		if (expandedRows.has(rowId)) {
			newExpandedRows.delete(rowId);
		} else {
			newExpandedRows.add(rowId);
			// Only fetch data when expanding, not when collapsing
			getRecordsByInputRecord(Number(rowId));
		}
		setExpandedRows(newExpandedRows);
	};

	const convertDynamicData = (record: RecordsDataSelectable) => {
		const fieldList = dynamicObjectToFieldConvertor(record.data)
		const collapseRow = collapsedRecords.find(c => c.id === record.id)
		return <div className='DynamicInputData'>
			<ul className={collapseRow?.collapsed ? STYLE_SMALL : STYLE_BIG}>
				{fieldList.map((field: IFieldDetail) =>
					<li key={field.label}><b>{field.label}</b>: {field.value}</li>
				)}
			</ul>
			<p className='MoreLess' onClick={(event: any) => changeInputStyle(event, record.id)}>{collapseRow?.collapsed ? "More" : "Less"}...</p>
		</div>
	}

	const changeInputStyle = (event: any, recordId: number) => {
		event.stopPropagation()
		const newCollapseList = collapsedRecords.map((collapsed: any) =>
			collapsed.id === recordId ?
				({ id: recordId, collapsed: !collapsed.collapsed }) : collapsed
		)
		setCollapsedRecords(newCollapseList)
	}

	const onRecordAction = (action: string): void => {
		const selectedRecordIds = records
			.filter(r => r.selected)
			.map((record: RecordsDataSelectable) => record.id)

		triggerRecordAction(action, selectedRecordIds, observations)
	}

	const onValidationFromDialog = (recordId: number, action: string, obs: string) => {
		observations[recordId] = obs
		setObservations(observations)
		triggerRecordAction(action, [recordId], observations)
	}

	const onCancelFromDialog = () => {

	}

	const onSave = (fields: any, recordId: number) => {
		let fieldsToSave: any = {}
		fields.forEach((field: any) => {
			fieldsToSave[field.label] = field.value
		})
		RecordService.editRecordsData(recordId, fieldsToSave).then((result: any) => {
			props.onPageChange(props.currentPage)
		})
	}

	function createDateWithFormat(dateString: string) {
		const date = parseISO(dateString)
		return format(date, "dd-MM-yyyy, HH: mm")
	}

	function GenericDialogButton({ action, actionText, isDisabled, appearance }: any) {
		const configurationBody: ConfirmationBodyText = {
			confirmationText: `Are you sure you want to execute the ${actionText} action to the selected records?`
		}
		const confirmationTrigger: ConfirmationTriggers = {
			onNoClick: () => { },
			onYesClick: () => onRecordAction(action)
		}
		const triggerProps: ITriggerProps = {
			appearance,
			icon: null,
			isDisabled,
			text: actionText
		}
		return <ConfirmationDialog confirmationBody={configurationBody} confirmationTriggers={confirmationTrigger} triggerProps={triggerProps} />
	}

	function ButtonsByView() {
		if (errorTable) {
			return (
				<div className='ButtonActions'>
					<GenericDialogButton action="cancel" actionText="Cancel" isDisabled={!anySelectedRecord()} />
					<GenericDialogButton appearance="primary" action="submit" actionText="Submit" isDisabled={!anySelectedRecord()} />
				</div>
			)
		}
		return (
			<div className='ButtonActions'>
				<GenericDialogButton action="cancel" actionText="Cancel" isDisabled={!anySelectedRecord()} onClick={() => onRecordAction(CANCEL_ACTION)} />
				<GenericDialogButton appearance='primary' action="invalidate" actionText="Invalidate" isDisabled={!anySelectedRecord()} onClick={() => onRecordAction(INVALIDATE_ACTION)} />
				<GenericDialogButton appearance='primary' action="validate" actionText="Validate" isDisabled={!anySelectedRecord()} onClick={() => onRecordAction(VALIDATE_ACTION)} />
			</div>
		)
	}

	const rows = sort(getRows());

	return (
		<div className='TodoRecordContainer'>
			<div className='TableActions'>
				<RowCounterSelector onCountSelected={onOptionSelected} rowCount={props.rowPerPage ?? ROWS_PER_PAGE[0]} />
				<ButtonsByView />
			</div>
			<Table
				ref={tableRef}
				sortable
				arial-label="Default table"
				{...columnSizing_unstable.getTableProps()}
			>
				<TableHeader>
					<TableRow className='TableHeader' >
						<TableSelectionCell
							onClick={onHeaderSelected}
							className='HeaderCell'
							checked={allCheck}
							checkboxIndicator={{ "aria-label": "Select all rows " }}
						/>
						<TableHeaderCell className='HeaderCell'></TableHeaderCell>
						{availableColumns.map((column) => {
							return (
								<TableHeaderCell
									{...getHeaderProps(column.columnId)}
									className='HeaderCell'
									key={column.columnId}
								>
									{column.renderHeaderCell()}
								</TableHeaderCell>
							)
						})}
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map((item) => {
						const isExpanded = expandedRows.has(item.item.id);

						return (
							<React.Fragment key={item.item.id}>
								<TableRow
									style={getRowStyle(item.item.id)}
								>
									<TableSelectionCell
										onClick={() => onRowSelected(item.item.id)}
										checked={item.item.selected}
										checkboxIndicator={{ "aria-label": "Select all rows " }}
									/>
									<TableCell className="expand-cell">
										<Button
											appearance="subtle"
											icon={isExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
											onClick={() => toggleRowExpansion(item.item.id)}
											aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
										/>
									</TableCell>
									<TableCell>
										<Link className='LinkStyle' to={`/${item.item.job_id}/${showStatus}`}>{item.item.job_id}</Link>
										<div
											className="row-resize-handle"
											onMouseDown={(e) => handleRowResize(item.item.id, e)}
										></div>
									</TableCell>
									<TableCell>
										{item.item.id}
										<div
											className="row-resize-handle"
											onMouseDown={(e) => handleRowResize(item.item.id, e)}
										></div>
									</TableCell>
									<TableCell>
										<TableCellLayout>
											{createDateWithFormat(item.item.created_on)}
											<div
												className="row-resize-handle"
												onMouseDown={(e) => handleRowResize(item.item.id, e)}
											></div>
										</TableCellLayout>
									</TableCell>
									<TableCell>
										<TableCellLayout truncate>
											{item.item.process}
											<div
												className="row-resize-handle"
												onMouseDown={(e) => handleRowResize(item.item.id, e)}
											></div>
										</TableCellLayout>
									</TableCell>
									<TableCell>
										<TableCellLayout truncate>
											{item.item.subprocess}
											<div
												className="row-resize-handle"
												onMouseDown={(e) => handleRowResize(item.item.id, e)}
											></div>
										</TableCellLayout>
									</TableCell>
									<TableCell>
										<TableCellLayout className='TableCellLayout'>
											{convertDynamicData(item.item)}
											<div
												className="row-resize-handle"
												onMouseDown={(e) => handleRowResize(item.item.id, e)}
											></div>
										</TableCellLayout>
									</TableCell>
									{errorTable && <TableCell></TableCell>}
									<TableCell>
										<TableCellLayout >
											<GenericDialog TemplateComponent={RecordsDialog}
												TemplateButtonIcon={EditRegular}
												TemplateButtonText=''
												TemplateButtonProps={null}
												templateProps={
													{
														data: item.item.data,
														errorsType: errorTable,
														onSave: (fields: any) => onSave(fields, item.item.id),
														onValidate: (observations: string) => onValidationFromDialog(item.item.id, VALIDATE_ACTION, observations),
														onInvalidate: (observations: string) => onValidationFromDialog(item.item.id, INVALIDATE_ACTION, observations),
														onCancel: onCancelFromDialog
													}
												}
												dialogName='Records Edit'
											/>
											<div
												className="row-resize-handle"
												onMouseDown={(e) => handleRowResize(item.item.id, e)}
											></div>
										</TableCellLayout>
									</TableCell>
								</TableRow>
								{isExpanded && (
									<TableRow>
										<TableCell colSpan={availableColumns.length + 2} className="expanded-content-cell">
											<div className="expanded-content">
												<TodoOutputsByRecords
													outputsRecords={outputRecordsByRow[item.item.id.toString()] || []}
												/>
											</div>
										</TableCell>
									</TableRow>
								)}
							</React.Fragment>
						);
					})}
				</TableBody>
			</Table>
			<Pagination
				className="pagination-bar"
				siblingCount={PAGINTATION_SIBLING_COUNT}
				totalCount={props.allRecordCount ?? 0}
				rowsPerPage={props.rowPerPage ?? ROWS_PER_PAGE[0]}
				onPageChange={page => props.onPageChange(page)}
				currentPage={props.currentPage}
			/>
		</div>
	)
};

export default TodoRecords;
